// Enum values are the API contract and stay English; their labels live in the `outbound`
// catalogue under the same keys.

export const OUTBOUND_API = 'outbound_calls';

export const CAMPAIGN_STATUSES = ['draft', 'running', 'paused', 'finished'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const ENTRY_STATUSES = [
  'pending',
  'calling',
  'reached',
  'not_reached',
  'failed',
  'skipped',
] as const;
export type EntryStatus = (typeof ENTRY_STATUSES)[number];

/** `callback` is a filter only: a pending entry with a callback booked. */
export const ENTRY_STATUS_FILTERS = [
  'pending',
  'callback',
  'calling',
  'reached',
  'not_reached',
  'failed',
  'skipped',
] as const;
export type EntryStatusFilter = (typeof ENTRY_STATUS_FILTERS)[number];

/** The outcomes a new campaign offers to retry. The API takes any disposition. */
export const RETRY_OUTCOMES = ['no_answer', 'busy', 'voicemail', 'failed'] as const;

/** Not enforced by the API: the LLM may post a value outside this list. */
export const DISPOSITIONS = [
  'reached',
  'voicemail',
  'wrong_person',
  'refused',
  'unresponsive',
  'incomplete',
  'no_answer',
  'busy',
  'failed',
] as const;
export type Disposition = (typeof DISPOSITIONS)[number];

/** The code the agent opens `conclusion` with, before a colon and its own words. */
export const CONCLUSION_CODES = [
  'not_interested',
  'no_budget',
  'has_provider',
  'not_decision_maker',
  'gatekeeper_blocked',
  'call_back_later',
  'send_offer_by_email',
  'do_not_call',
  'salesperson_no_answer',
  'salesperson_unavailable',
  'expert_requested',
  'transferred_to_expert',
] as const;
export type ConclusionCode = (typeof CONCLUSION_CODES)[number];

/** How the hand-over to a salesperson ended; the latest attempt wins. */
export const TRANSFER_RESULTS = ['connected', 'no_answer', 'unavailable', 'failed'] as const;
export type TransferResult = (typeof TRANSFER_RESULTS)[number];

export const TRIGGER_SOURCES = ['crm_panel', 'api', 'notification', 'campaign'] as const;
export type TriggerSource = (typeof TRIGGER_SOURCES)[number];

export const SALES_AGENT_STATUSES = ['offline', 'available', 'busy'] as const;
export type SalesAgentStatus = (typeof SALES_AGENT_STATUSES)[number];

/** Why the import reported a row, in the order the report groups them. */
export const IMPORT_ISSUES = [
  'status_note',
  'summed_numbers',
  'no_phone',
  'company_phone',
  'contact_phone',
  'contact_without_phone',
  'missing_name',
  'invalid_nip',
  'invalid_value',
  'lost_letters',
] as const;
export type ImportIssue = (typeof IMPORT_ISSUES)[number];

/** ISO weekdays, Monday = 1, as `call_days` and `work_days` store them. */
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

// The API's defaults, so a new campaign starts from what it would save without them.
export const CAMPAIGN_DEFAULTS = {
  window_start: '09:00',
  window_end: '16:00',
  call_days: [1, 2, 3, 4, 5],
  max_attempts: 3,
  retry_delay_minutes: 120,
  retry_on: ['no_answer', 'busy', 'voicemail'],
  max_callbacks: 2,
  pause_between_calls_seconds: 15,
};

// Model validators, mirrored so the form refuses what the API would 422.
export const CAMPAIGN_LIMITS = {
  maxAttempts: { min: 1, max: 10 },
  retryDelayMinutes: { min: 1, max: 7 * 24 * 60 },
  maxCallbacks: { min: 0, max: 10 },
  pauseSeconds: { min: 0, max: 3600 },
};

/** The outbound prompt reads at most this many keys from `data_to_collect`. */
export const MAX_DATA_TO_COLLECT_KEYS = 4;

/**
 * No WebSocket carries these statuses, so a screen that shows one asks again this often:
 * salespeople, the header toggle and a running campaign.
 */
export const OUTBOUND_POLL_INTERVAL_MS = 5_000;

/** The API's own page size for entries, and the step "Pokaż więcej" grows by. */
export const OUTBOUND_PAGE_SIZE = 12;

/** `items_per_page` is clamped here by the API, silently. */
export const OUTBOUND_MAX_ROWS = 200;
